import { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Archive, Briefcase, Loader2, Plus, Trash2, FilePlus2, FileDown, QrCode, X, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { fmtDateMtl } from "../lib/dateMtl";
import { useAuth } from "../context/AuthContext";
import { filtrerClients, doublonsProbables, adressePreferee, normaliseTel } from "../lib/rechercheClient";
import { SEO } from "../components/SEO";
import { FadeUp } from "../components/motion";
import { formatPhone } from "../lib/utils";  // format auto (123) 456-7890 (règle 2026-10-22)

const money = (v) => `${(v || 0).toFixed(2)} $`;
const fmtDate = (iso) => fmtDateMtl(iso);  // date à Montréal (règle permanente)
const STEPS = [["repondu", "Répondu"], ["accepte", "Accepté"], ["montage_recu", "Texte reçu"],
               ["paye", "Payé"], ["commande", "Commandé"], ["expedie", "Expédié"], ["livre", "Livré"]];

// Portail VENDEUR (demande du propriétaire) : le vendeur ne voit QUE les dossiers
// que le propriétaire a cochés pour lui (soumissions « visibles par le vendeur »
// + dossiers de ses clients assignés). Si la case « Créer des factures » est
// cochée, il peut créer des factures/soumissions qui arrivent dans l'admin.
export default function Vendeur() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [mesClients, setMesClients] = useState([]);
  // Récap PDF : sélecteur de mois (demande du propriétaire, 2026-10-22) — les 12
  // derniers mois, « mois courant » par défaut
  const [recapMois, setRecapMois] = useState("");
  const moisOptions = useMemo(() => {
    const noms = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet",
                  "août", "septembre", "octobre", "novembre", "décembre"];
    const out = [{ v: "", label: "Mois courant" }];
    const d = new Date();
    for (let i = 0; i < 12; i++) {
      out.push({ v: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
                 label: `${noms[d.getMonth()]} ${d.getFullYear()}` });
      d.setMonth(d.getMonth() - 1);
    }
    return out;
  }, []);
  const [formOpen, setFormOpen] = useState(false);
  // COPIE CONFORME de la fiche admin (règle du propriétaire, 2026-10-22 —
  // « la fiche côté vendeur doit être la même que côté admin, copier-coller ») :
  // mêmes champs, même ordre, mêmes cases obligatoires
  const CLIENT_VIDE = { name: "", organization: "", address1: "", address2: "", city: "",
                        province: "", postal_code: "", phone: "", email: "",
                        needed_by: "", invoice_no: "", delivery_date: "", country: "" };
  const [client, setClient] = useState(CLIENT_VIDE);
  const [adressesClient, setAdressesClient] = useState([]);  // adresses du compte client choisi
  const [rechercheClient, setRechercheClient] = useState("");  // recherche par nom/courriel/adresse/téléphone
  const [choixOuvert, setChoixOuvert] = useState(false);
  const [resultatsServeur, setResultatsServeur] = useState(null);  // recherche sur TOUS les comptes
  // Recherche serveur (anti-rebond 300 ms) : couvre aussi les vieux comptes
  // au-delà des 200 récents chargés d'office, et les adresses/téléphones
  // enregistrés (demande du propriétaire, 2026-10-16)
  useEffect(() => {
    const t = rechercheClient.trim();
    if (t.length < 2) { setResultatsServeur(null); return; }
    const h = setTimeout(() => {
      api.get("/vendeur/clients", { params: { q: t } })
        .then(({ data }) => setResultatsServeur(data.clients || []))
        .catch(() => setResultatsServeur([]));
    }, 300);
    return () => clearTimeout(h);
  }, [rechercheClient]);
  // Alerte doublon sur TOUS les comptes (demande du propriétaire, 2026-10-16) :
  // on interroge le serveur avec la boîte du courriel saisi, puis on compare
  const [doublons, setDoublons] = useState([]);
  useEffect(() => {
    const em = client.email.trim().toLowerCase();
    if (!em.includes("@") || em.split("@")[0].length < 3) { setDoublons([]); return; }
    const h = setTimeout(() => {
      api.get("/vendeur/clients", { params: { q: em.split("@")[0] } })
        .then(({ data }) => setDoublons(doublonsProbables(data.clients || [], em, client.phone)))
        .catch(() => {});
    }, 400);
    return () => clearTimeout(h);
  }, [client.email, client.phone]);  // eslint-disable-line react-hooks/exhaustive-deps
  const [lines, setLines] = useState([{ catalogue: "", product_name: "", product_no: "", qty: 1, product_price: "" }]);
  const [busy, setBusy] = useState(false);
  const [qrFor, setQrFor] = useState(null);  // {id, img, amount, name} — QR de paiement en personne
  const [qrBusy, setQrBusy] = useState("");

  const load = () => api.get("/vendeur/dossiers").then(({ data: d }) => setData(d))
    .catch(() => setData({ dossiers: [], sections: [], actions: [] }));
  const isStaff = user?.role === "vendeur" || user?.role === "admin";
  useEffect(() => {
    if (!isStaff) return;
    load();
    api.get("/vendeur/clients").then(({ data: d }) => setMesClients(d.clients || [])).catch(() => {});
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  if (user === false) return <Navigate to="/connexion" state={{ next: "/vendeur" }} replace />;
  if (!user) return null;
  if (!isStaff) {
    return (
      <div className="pt-40 pb-28 text-center text-ivory/70" data-testid="vendeur-denied">
        Accès réservé aux vendeurs. <Link to="/" className="text-gold hover:underline">Retour à l'accueil</Link>
      </div>
    );
  }

  const dossiers = data?.dossiers || [];
  // Création d'une facture/fiche (demande du propriétaire, 2026-10-22) : permise
  // aussi aux vendeurs qui ont la section Soumissions en mode ÉDITION, pas
  // seulement à ceux qui ont la case « Créer des factures »
  const canCreate = (data?.actions || []).includes("creer_facture")
    || data?.sections?.soumissions === "edition" || user?.role === "admin";
  // Boutons d'étapes (demande du propriétaire) : visibles ET cliquables quand
  // l'accès admin l'autorise — vendeur avec « Soumissions » en mode ÉDITION,
  // ou admin. En lecture seule : les pastilles restent, sans boutons.
  const canEdit = user?.role === "admin" || data?.sections?.soumissions === "edition";
  // « Expédié » reste à l'admin (passe par /ship : courriels de suivi) ;
  // « accepte », « montage_recu » et « livre » envoient un courriel au client → confirmation.
  const STEPS_EMAIL = new Set(["accepte", "montage_recu", "livre"]);
  const CLICKABLE = STEPS.filter(([k]) => k !== "expedie");

  const marquerEtape = async (d, key, lab) => {
    if (STEPS_EMAIL.has(key) && !window.confirm(`Marquer « ${lab} » envoie un courriel au client ${d.email}. Continuer ?`)) return;
    try {
      const r = await api.patch(`/admin/quote-requests/${d.id}/status`, { key, value: true });
      toast.success(r.data?.emailed ? `« ${lab} » coché — courriel envoyé au client.` : `« ${lab} » coché.`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  // « Encaisser » (demande du propriétaire) : ouvre la page de paiement Stripe du
  // dossier — carte, Apple Pay, Google Pay — pour un paiement sur place en quelques
  // secondes. Le lien en attente est réutilisé (jamais de session en double).
  const encaisser = async (d) => {
    try {
      const r = await api.post(`/admin/quote-requests/${d.id}/payment-link`);
      window.open(r.data.url, "_blank", "noopener");
      toast.success(`Page de paiement ouverte — ${money(r.data.amount)}. Le dossier passera à « Payé » tout seul.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  // QR code de paiement EN PERSONNE (demande du propriétaire) : le client scanne
  // le code avec SON téléphone et paie sur place — le paiement reste lié au
  // dossier (« Payé » automatique), contrairement au Tap to Pay de l'app Stripe.
  const showQr = async (d) => {
    setQrBusy(d.id);
    try {
      const resp = await api.get(`/admin/quote-requests/${d.id}/payment-qr.png`, { responseType: "blob" });
      setQrFor({ id: d.id, img: URL.createObjectURL(resp.data), amount: d.total, name: d.name });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setQrBusy("");
    }
  };
  const subtotal = lines.reduce((s, l) => s + (parseFloat(l.product_price) || 0) * (parseInt(l.qty) || 1), 0);
  const tps = Math.round(subtotal * 0.05 * 100) / 100;
  const tvq = Math.round(subtotal * 0.09975 * 100) / 100;

  // Choisir un client remplit ses champs, dont l'ADRESSE depuis son compte
  // (demande du propriétaire, 2026-10-16) — la première adresse enregistrée par
  // défaut ; s'il en a plusieurs, un sélecteur permet d'en choisir une autre.
  const appliqueAdresse = (a) => {
    if (!a) return;
    setClient((f) => ({ ...f,
      address1: a.address1 || f.address1, address2: a.address2 ?? f.address2,
      city: a.city || f.city, province: a.province || f.province,
      postal_code: a.postal_code || f.postal_code, country: a.country || f.country,
      phone: f.phone || a.phone || "" }));
  };
  const pickClient = (email) => {
    const c = mesClients.find((x) => x.email === email);
    if (!c) { setAdressesClient([]); return; }
    setClient((f) => ({ ...f, name: c.name || f.name, email: c.email, phone: c.phone || "",
                        city: c.city || "", organization: c.organization || "" }));
    const ads = c.addresses || [];
    setAdressesClient(ads);
    const preferee = adressePreferee(ads);  // adresse par défaut du compte d'abord
    if (preferee) appliqueAdresse(preferee);
  };

  const setLine = (i, k, v) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));

  const submitFacture = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/vendeur/factures", { ...client, items: lines.filter((l) => l.product_name.trim()) });
      toast.success(r.data?.compte_cree
        ? "Facture créée — et un compte a été créé pour ce client : il reçoit un courriel pour choisir son mot de passe."
        : "Facture créée — elle est maintenant dans « Ventes & Soumissions » de l'admin.");
      setFormOpen(false);
      setClient(CLIENT_VIDE);
      setAdressesClient([]);
      setRechercheClient("");
      setLines([{ catalogue: "", product_name: "", product_no: "", qty: 1, product_price: "" }]);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    setBusy(false);
  };

  return (
    <div data-testid="vendeur-page" className="max-w-5xl mx-auto px-6 lg:px-10 pt-32 pb-28">
      <SEO title="Portail vendeur | Mr Trophée" path="/vendeur" noindex />
      <FadeUp>
        <p className="text-gold tracking-[0.3em] uppercase text-xs mb-4 flex items-center gap-2">
          <Briefcase className="w-4 h-4" /> Portail vendeur
        </p>
        <h1 className="font-serif text-5xl md:text-6xl mb-3">Mes dossiers</h1>
        <p className="text-sm text-ivory/60 mb-10">
          {user.name || user.email} — {user.role === "admin"
            ? "vous voyez tous les dossiers en cours (les archivés restent dans l'admin)."
            : "vous ne voyez que les dossiers choisis pour vous par Mr Trophée."}
          <Link to="/guide-vendeur" data-testid="vendeur-guide-link"
            className="text-gold hover:underline ml-2">Guide du vendeur</Link>
        </p>
      </FadeUp>

      {/* AVIS 2FA du personnel (demande du propriétaire, 2026-10-23) : délai de
          grâce de 30 jours PAR COMPTE — le compte à rebours s'affiche aussi sur
          le portail vendeur (sinon un nouveau vendeur serait bloqué sans prévenir) */}
      {!user?.totp_enabled && user?.twofa_required_after
        && !window.location.hostname.includes("preview.emergentagent.com") && (
        <div data-testid="vendeur-2fa-notice"
          className="mb-8 border border-amber-400/40 bg-amber-400/10 rounded-xl px-5 py-4 flex flex-wrap items-center gap-3">
          <ShieldAlert className="w-5 h-5 text-amber-300 shrink-0" />
          <p className="text-sm text-amber-200 flex-1 min-w-[260px]">
            <strong>Sécurité :</strong> la double vérification (2FA) deviendra <strong>obligatoire
            le {new Date(user.twofa_required_after + "T00:00:00").toLocaleDateString("fr-CA", { day: "numeric", month: "long", year: "numeric" })}</strong>
            {" "}— dans {Math.max(0, Math.ceil((new Date(user.twofa_required_after + "T00:00:00") - Date.now()) / 86400000))} jours.
            Activez-la dès maintenant dans l'administration (Paramètres) pour éviter tout blocage à la connexion.
          </p>
        </div>
      )}

      {/* Aide-mémoire paiement en personne (demande du propriétaire) : le Tap to Pay
          de l'app Stripe encaisse l'argent SANS le lier à un dossier — il n'apparaît
          jamais ici ni dans l'admin. Pour un paiement suivi : Encaisser ou QR code. */}
      {canEdit && (
        <div className="mb-8 rounded-xl border border-amber-400/30 bg-amber-400/5 p-5" data-testid="vendeur-tap-to-pay-memo">
          <p className="text-sm font-semibold text-amber-300 mb-1">
            Paiement en personne : utilisez « Encaisser » ou le « QR code » du dossier
          </p>
          <p className="text-xs text-ivory/70 leading-relaxed">
            Le <strong>Tap to Pay de l'application Stripe</strong> encaisse l'argent, mais il ne se
            lie <strong>jamais</strong> à un dossier : rien n'apparaît ici ni dans l'admin, et aucune
            facture ne part au client. Avec « Encaisser » ou le « QR code », le client paie sur la
            page du dossier et « Payé » se coche tout seul.
          </p>
          <details className="mt-2 text-xs text-ivory/55">
            <summary className="cursor-pointer text-amber-300/80 hover:text-amber-200 transition-colors"
              data-testid="vendeur-tap-to-pay-details">
              Si vous utilisez quand même Tap to Pay (directives Stripe — non lié au dossier)
            </summary>
            <ol className="list-decimal ml-5 mt-2 space-y-1 leading-relaxed">
              <li>Ouvrez l'application mobile Stripe Dashboard.</li>
              <li>Touchez le symbole d'ajout (+) dans n'importe quel onglet.</li>
              <li>Sélectionnez « Débiter une carte bancaire ou envoyer une facture ».</li>
              <li>Saisissez le montant à débiter.</li>
              <li>Sélectionnez « Tap to Pay » comme option d'acceptation.</li>
              <li>Invitez le client à présenter sa carte à l'appareil, puis suivez les instructions à l'écran.</li>
              <li>Notez le dossier à la main — le paiement ne sera PAS lié automatiquement.</li>
            </ol>
          </details>
        </div>
      )}

      {canCreate && (
        <div className="mb-10">
          <div className="flex flex-wrap items-center gap-3">
          <button onClick={() => setFormOpen((v) => !v)} data-testid="vendeur-new-facture"
            className="inline-flex items-center gap-2 bg-gold text-ink font-semibold rounded-full px-6 py-3 text-sm hover:bg-champagne transition-colors duration-300">
            <FilePlus2 className="w-4 h-4" /> {formOpen ? "Fermer le formulaire" : "Nouvelle facture / soumission"}
          </button>
          {/* RÉCAP DU MOIS (demande du propriétaire, 2026-10-22) : PDF des ventes
              du vendeur à remettre à sa direction — sélecteur de mois (12 derniers) */}
          <select value={recapMois} onChange={(e) => setRecapMois(e.target.value)}
            data-testid="vendeur-recap-mois" aria-label="Mois du récapitulatif"
            className="bg-surface border border-gold/40 text-ivory/85 rounded-full px-4 py-3 text-sm focus:outline-none focus:border-gold transition-colors">
            {moisOptions.map((o) => <option key={o.v || "courant"} value={o.v}>{o.label}</option>)}
          </select>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/vendeur/recap-pdf${recapMois ? `?mois=${recapMois}` : ""}`}
            target="_blank" rel="noreferrer"
            data-testid="vendeur-recap-pdf"
            className="inline-flex items-center gap-2 border border-gold/40 text-gold rounded-full px-6 py-3 text-sm hover:bg-gold/10 transition-colors duration-300">
            <FileDown className="w-4 h-4" /> Récap du mois (PDF)
          </a>
          </div>

          {formOpen && (
            <form onSubmit={submitFacture} data-testid="vendeur-facture-form"
              className="mt-5 bg-surface border border-gold/15 rounded-xl p-6 space-y-5">
              {/* Recherche client (demande du propriétaire, 2026-10-16) : par
                  nom, prénom, courriel, adresse enregistrée ou no de téléphone ;
                  si aucun compte n'existe, il sera créé à la volée avec
                  invitation par courriel (« Nouveau client rapide »). */}
              <div className="relative" onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) setChoixOuvert(false);
              }}>
                <label className="block text-sm text-ivory/70 mb-1.5">Client — nom, prénom, courriel, association, adresse ou no de téléphone (remplit les champs)</label>
                <input value={rechercheClient}
                  onChange={(e) => { setRechercheClient(e.target.value); setChoixOuvert(true); }}
                  onFocus={() => setChoixOuvert(true)}
                  placeholder="Nom, courriel, association, adresse ou téléphone du client…"
                  data-testid="vendeur-client-search"
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none transition-colors duration-300" />
                {choixOuvert && rechercheClient.trim() && (() => {
                  const resultats = resultatsServeur ?? filtrerClients(mesClients, rechercheClient);
                  return resultats.length > 0 ? (
                    <div className="absolute z-20 mt-1 w-full bg-ink border border-ivory/15 rounded-lg max-h-56 overflow-y-auto shadow-xl"
                      data-testid="vendeur-client-resultats">
                      {resultats.map((c) => (
                        <button type="button" key={c.email} data-testid={`vendeur-client-choix-${c.email}`}
                          onClick={() => { pickClient(c.email); setRechercheClient(`${c.name} — ${c.email}`); setChoixOuvert(false); }}
                          className="w-full text-left px-4 py-2.5 text-sm hover:bg-surface transition-colors duration-200 border-b border-ivory/5 last:border-0">
                          <span className="text-ivory">{c.name || "(sans nom)"}</span>
                          <span className="text-ivory/50"> — {c.email}</span>
                          {c.phone && <span className="text-ivory/40"> · {c.phone}</span>}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-2 text-xs text-gold/80" data-testid="vendeur-client-aucun">
                      Aucun compte pour cette recherche — remplissez les champs ci-dessous : un compte
                      sera créé pour ce client et il recevra un courriel pour choisir son mot de passe.
                    </p>
                  );
                })()}
              </div>
              {/* COPIE CONFORME de la fiche admin (règle du propriétaire,
                  2026-10-22 — « copier-coller sans modification ») : mêmes champs,
                  même ordre, mêmes cases OBLIGATOIRES (nom complet, adresse, ville,
                  code postal, téléphone, courriel ET « livrer avant le ») */}
              <div className="grid sm:grid-cols-2 gap-3">
                {[["name", "Nom complet *", true], ["organization", "Organisation", false],
                  ["address1", "Adresse *", true], ["address2", "Adresse (suite)", false],
                  ["city", "Ville *", true], ["province", "Prov.", false],
                  ["postal_code", "Code postal *", true], ["phone", "Téléphone *", true],
                  ["email", "Courriel *", true], ["needed_by", "Livrer avant le *", true],
                  ["invoice_no", "Nº de facture", false], ["delivery_date", "Livraison prévue le", false]]
                  .map(([k, lab, req]) => (
                  <label key={k} className="block">
                    <span className="block text-sm text-ivory/70 mb-1.5">{lab}</span>
                    <input value={client[k] || ""} required={req}
                      type={k === "email" ? "email" : k === "phone" ? "tel"
                            : (k === "needed_by" || k === "delivery_date") ? "date" : "text"}
                      multiple={k === "email" || undefined}
                      minLength={k === "phone" ? 7 : k === "address1" ? 3 : k === "city" ? 2
                                 : k === "postal_code" ? 6 : undefined}
                      maxLength={k === "postal_code" ? 7 : undefined}
                      onChange={(e) => setClient((f) => ({ ...f, [k]: k === "phone" ? formatPhone(e.target.value) : e.target.value }))}
                      data-testid={`vendeur-facture-${k}`}
                      className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none transition-colors duration-300 [color-scheme:dark]" />
                    {k === "email" && (
                      <span className="block text-[10px] text-ivory/40 mt-1" data-testid="vendeur-facture-email-hint">
                        Plusieurs courriels possibles — séparez-les par des virgules.
                      </span>
                    )}
                  </label>
                ))}
              </div>

              {/* Alerte doublon (demande du propriétaire, 2026-10-16) : avant de
                  créer un compte à la volée, on avertit si un compte semblable
                  existe déjà — même boîte sur un autre domaine, faute de frappe
                  dans le même domaine, ou même numéro de téléphone. */}
              {doublons.length > 0 && (
                <div className="bg-amber-500/10 border border-amber-400/30 rounded-lg px-4 py-3 text-xs text-amber-200"
                  data-testid="vendeur-doublon-alerte">
                  <p className="font-semibold mb-1">Un compte semblable existe déjà — vérifiez avant de créer un doublon :</p>
                  {doublons.map((d) => (
                    <p key={d.email}>• {d.name || "(sans nom)"} — {d.email}{d.phone ? ` · ${d.phone}` : ""}</p>
                  ))}
                </div>
              )}

              {/* Adresse du client — remplie automatiquement depuis son compte
                  (demande du propriétaire, 2026-10-16), modifiable à la main */}
              {adressesClient.length > 1 && (
                <label className="block">
                  <span className="block text-sm text-ivory/70 mb-1.5">Adresse enregistrée du client</span>
                  <select defaultValue={adressesClient[0]?.id || ""}
                    onChange={(e) => appliqueAdresse(adressesClient.find((a) => a.id === e.target.value))}
                    data-testid="vendeur-adresse-select"
                    className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none [color-scheme:dark]">
                    {adressesClient.map((a, i) => (
                      <option key={a.id || i} value={a.id || i}>
                        {(a.label ? `${a.label} — ` : "") + [a.address1, a.city, a.postal_code].filter(Boolean).join(", ")}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {/* (L'adresse fait maintenant partie de la grille unique ci-dessus —
                  copie conforme de la fiche admin, 2026-10-22) */}

              <div>
                <p className="text-[11px] uppercase tracking-widest text-gold mb-2">Produits</p>
                {lines.map((l, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 mb-2" data-testid={`vendeur-line-${i}`}>
                    {/* Catalogue en LISTE DÉROULANTE (demande du propriétaire,
                        2026-10-22) — comme côté admin, jamais de texte libre */}
                    <select value={l.catalogue} onChange={(e) => setLine(i, "catalogue", e.target.value)}
                      data-testid={`vendeur-line-cat-${i}`}
                      className="col-span-2 bg-ink border border-ivory/15 rounded-lg px-2 py-2.5 text-xs focus:border-gold focus:outline-none [color-scheme:dark]">
                      <option value="">Catalogue</option>
                      {["1", "2", "3", "4", "5", "6"].map((c) => <option key={c} value={c}>Catalogue {c}</option>)}
                    </select>
                    <input value={l.product_name} onChange={(e) => setLine(i, "product_name", e.target.value)} placeholder="Description *" required
                      data-testid={`vendeur-line-name-${i}`}
                      className="col-span-4 bg-ink border border-ivory/15 rounded-lg px-3 py-2.5 text-xs focus:border-gold focus:outline-none" />
                    <input value={l.product_no} onChange={(e) => setLine(i, "product_no", e.target.value)} placeholder="Nº"
                      className="col-span-2 bg-ink border border-ivory/15 rounded-lg px-3 py-2.5 text-xs focus:border-gold focus:outline-none" />
                    <input value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} type="number" min="1" placeholder="Qté"
                      data-testid={`vendeur-line-qty-${i}`}
                      className="col-span-1 bg-ink border border-ivory/15 rounded-lg px-2 py-2.5 text-xs focus:border-gold focus:outline-none" />
                    <input value={l.product_price} onChange={(e) => setLine(i, "product_price", e.target.value)} type="number" min="0" step="0.01" placeholder="Prix"
                      data-testid={`vendeur-line-price-${i}`}
                      className="col-span-2 bg-ink border border-ivory/15 rounded-lg px-2 py-2.5 text-xs focus:border-gold focus:outline-none" />
                    <button type="button" onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}
                      disabled={lines.length <= 1} data-testid={`vendeur-line-del-${i}`}
                      className="col-span-1 flex items-center justify-center text-red-400/80 hover:text-red-400 disabled:opacity-30">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <button type="button" onClick={() => setLines((ls) => [...ls, { catalogue: "", product_name: "", product_no: "", qty: 1, product_price: "" }])}
                  data-testid="vendeur-line-add"
                  className="inline-flex items-center gap-1.5 text-xs text-gold hover:text-champagne transition-colors mt-1">
                  <Plus className="w-3.5 h-3.5" /> Ajouter une ligne
                </button>
              </div>

              <p className="text-sm text-ivory/70 border-t border-ivory/10 pt-4" data-testid="vendeur-facture-totals">
                Sous-total <strong className="text-gold">{money(subtotal)}</strong>
                {" · "}TPS {money(tps)}{" · "}TVQ {money(tvq)}
                {" · "}Total <strong className="text-gold">{money(subtotal + tps + tvq)}</strong>
              </p>
              <button type="submit" disabled={busy} data-testid="vendeur-facture-submit"
                className="inline-flex items-center gap-2 bg-gold text-ink font-semibold rounded-full px-6 py-3 text-sm hover:bg-champagne transition-colors duration-300 disabled:opacity-60">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} Créer la facture
              </button>
            </form>
          )}
        </div>
      )}

      {data === null ? (
        <p className="text-ivory/50 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</p>
      ) : dossiers.length === 0 ? (
        <p className="text-sm text-ivory/50 bg-surface border border-gold/15 rounded-xl p-7" data-testid="vendeur-empty">
          Aucun dossier pour l'instant — Mr Trophée vous en assignera (ou créez une facture si l'action vous est permise).
        </p>
      ) : (
        <div className="space-y-4">
          {dossiers.map((d) => (
            <div key={d.id} data-testid={`vendeur-dossier-${d.id}`}
              className="bg-surface border border-gold/15 rounded-xl p-5">
              <div className="flex flex-wrap items-start gap-x-4 gap-y-1">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-ivory" data-testid={`vendeur-dossier-name-${d.id}`}>
                    {d.name}
                    {d.paye ? (
                      <span className="ml-2 text-[9px] uppercase tracking-widest bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 rounded-full px-2 py-0.5 align-middle">Vente payée</span>
                    ) : (
                      <span className="ml-2 text-[9px] uppercase tracking-widest bg-amber-500/20 text-amber-300 border border-amber-500/40 rounded-full px-2 py-0.5 align-middle">En cours</span>
                    )}
                    {d.creee_par_moi && (
                      <span className="ml-2 text-[9px] uppercase tracking-widest bg-sky-500/20 text-sky-300 border border-sky-500/40 rounded-full px-2 py-0.5 align-middle">Créée par moi</span>
                    )}
                  </p>
                  <p className="text-xs text-ivory/55">{[d.organization, d.city].filter(Boolean).join(" · ")}</p>
                  <p className="text-xs text-ivory/60 mt-0.5">{d.email}{d.phone ? ` · ${d.phone}` : ""}</p>
                  <p className="text-xs text-ivory/60 mt-1">
                    {(d.items || []).length} article{(d.items || []).length > 1 ? "s" : ""}
                    {" · "}<strong className="text-gold">{money(d.avant_taxes)}</strong> avant taxes
                    {" · "}<strong className="text-gold">{money(d.total)}</strong> après taxes
                  </p>
                </div>
                <span className="text-xs text-ivory/45 shrink-0">{fmtDate(d.created_at)}</span>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {STEPS.filter(([k]) => d.status?.[k]).map(([k, lab]) => (
                  <span key={k} className="text-[9px] uppercase tracking-widest border border-gold/30 text-gold rounded-full px-2 py-0.5">{lab}</span>
                ))}
                {/* Date de livraison visible par le vendeur (demande du propriétaire) */}
                {d.livre_le && (
                  <span className="text-[9px] uppercase tracking-widest border border-emerald-500/40 text-emerald-300 rounded-full px-2 py-0.5"
                    data-testid={`vendeur-livre-le-${d.id}`}>
                    Livrée le {fmtDate(d.livre_le)}
                  </span>
                )}
                {/* Avis d'épreuve (demande du propriétaire, 2026-10-06) : le vendeur
                    suit la réponse du client — bleu envoyée, rouge modifications,
                    or acceptée */}
                {d.proof_status === "envoyee" && (
                  <span className="text-[9px] uppercase tracking-widest border border-sky-400/40 text-sky-300 rounded-full px-2 py-0.5"
                    data-testid={`vendeur-proof-${d.id}`}>
                    Épreuve envoyée — en attente du client
                  </span>
                )}
                {d.proof_status === "modifications" && (
                  <span className="text-[9px] uppercase tracking-widest border border-red-400/40 text-red-300 rounded-full px-2 py-0.5"
                    data-testid={`vendeur-proof-${d.id}`}>
                    Épreuve : modifications demandées
                  </span>
                )}
                {d.proof_status === "acceptee" && (
                  <span className="text-[9px] uppercase tracking-widest border border-gold/50 text-gold rounded-full px-2 py-0.5"
                    data-testid={`vendeur-proof-${d.id}`}>
                    Épreuve acceptée
                  </span>
                )}
              </div>
              {/* Commentaire du client sur l'épreuve (utile pour le suivi vendeur) */}
              {d.proof_status === "modifications" && d.proof_comment && (
                <p className="mt-1.5 text-[11px] text-red-300/90 border-l-2 border-red-400/40 pl-2"
                  data-testid={`vendeur-proof-comment-${d.id}`}>
                  « {d.proof_comment} »
                </p>
              )}
              {canEdit && !d.paye && d.total > 0 && (
                <div className="mt-3 pt-3 border-t border-ivory/8 flex flex-wrap items-center gap-2">
                  <button type="button" onClick={() => encaisser(d)}
                    data-testid={`vendeur-encaisser-${d.id}`}
                    className="inline-flex items-center gap-2 bg-gold text-ink font-semibold rounded-full px-5 py-2 text-xs uppercase tracking-widest hover:bg-champagne transition-colors duration-300">
                    Encaisser {money(d.total)}
                  </button>
                  {/* Paiement EN PERSONNE par QR code (demande du propriétaire) :
                      le client scanne avec son téléphone — dossier lié automatiquement */}
                  <button type="button" onClick={() => showQr(d)} disabled={qrBusy === d.id}
                    data-testid={`vendeur-qr-${d.id}`}
                    title="Paiement en personne : le client scanne ce QR code avec son téléphone et paie — le dossier passe à « Payé » automatiquement"
                    className="inline-flex items-center gap-2 border border-gold/50 text-gold font-semibold rounded-full px-5 py-2 text-xs uppercase tracking-widest hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-60">
                    {qrBusy === d.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <QrCode className="w-3.5 h-3.5" />}
                    QR code
                  </button>
                </div>
              )}
              {canEdit && CLICKABLE.some(([k]) => !d.status?.[k]) && (
                <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-ivory/8" data-testid={`vendeur-actions-${d.id}`}>
                  {CLICKABLE.filter(([k]) => !d.status?.[k]).map(([k, lab]) => (
                    <button key={k} type="button" onClick={() => marquerEtape(d, k, lab)}
                      data-testid={`vendeur-step-${d.id}-${k}`}
                      className="text-[10px] uppercase tracking-widest border border-ivory/25 text-ivory/70 rounded-full px-3 py-1.5 hover:border-gold hover:text-gold transition-colors duration-300">
                      {lab}
                    </button>
                  ))}
                </div>
              )}
              {/* Archiver un dossier depuis le portail (demande du propriétaire,
                  2026-10-06) : range le dossier sans le supprimer — il disparaît
                  de cette liste et reste dans l'admin du propriétaire */}
              {canEdit && (
                <div className="mt-2.5 flex justify-end">
                  <button type="button" onClick={async () => {
                    if (!window.confirm(`Archiver le dossier de ${d.name} ? Il disparaîtra de votre liste (sans être supprimé — le propriétaire le reverra dans son admin).`)) return;
                    try {
                      await api.patch(`/admin/quote-requests/${d.id}/status`, { key: "archive", value: true });
                      toast.success("Dossier archivé — il a quitté votre liste.");
                      load();
                    } catch (err) {
                      toast.error(formatApiError(err.response?.data?.detail));
                    }
                  }}
                    data-testid={`vendeur-archiver-${d.id}`}
                    className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-ivory/45 hover:text-gold transition-colors duration-300">
                    <Archive className="w-3 h-3" /> Archiver ce dossier
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* QR code de paiement EN PERSONNE : le client scanne avec son téléphone */}
      {qrFor && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="vendeur-qr-modal">
          <button className="absolute inset-0 bg-ink/85 backdrop-blur-sm" onClick={() => setQrFor(null)} aria-label="Fermer" />
          <div className="relative bg-white rounded-2xl w-full max-w-sm p-6 shadow-2xl shadow-black/60 text-center">
            <button onClick={() => setQrFor(null)} data-testid="vendeur-qr-close"
              className="absolute top-3 right-3 text-neutral-400 hover:text-neutral-800 transition-colors z-10" aria-label="Fermer">
              <X className="w-5 h-5" />
            </button>
            <h3 className="font-serif text-xl text-neutral-900 mb-1">Paiement en personne</h3>
            <p className="text-xs text-neutral-500 mb-4">
              {qrFor.name} — <strong>{money(qrFor.amount)}</strong> taxes incluses.
              Le client scanne ce code avec son téléphone et paie par carte, Apple Pay ou Google Pay.
            </p>
            <img src={qrFor.img} alt="QR code de paiement" data-testid="vendeur-qr-image"
              className="w-64 h-64 mx-auto" />
            <p className="text-[11px] text-neutral-500 mt-4 leading-snug">
              Dès le paiement, le dossier passe à « Payé » tout seul —<br />
              contrairement au Tap to Pay de l'app Stripe, qui ne se lie à aucun dossier.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
